
import React from "react";
import { Schemes, Utils } from "../../../../../../../utils";
import { i18nManager } from "../../../../../../../i18n";
import * as ReactRouterDom from "react-router-dom";
import { Elements } from "../../../../../../shared/Elements";
import MediaInput from "../../../../../../shared/MediaInput";
import StatefulLink from "../../../../../../shared/StatefulLink";
import { ConfirmDialog, Siren } from "../../../../../../shared/ConfirmDialog";
import LoadingGif from "../../../../assets/images/loading_circle.gif";
import { Button, ButtonManageRef, Checkbox, DateHelper, DateTimeInput, Dropdown, Form, FormControl, Scheme, TextAreaInput, TextInput, useScreenDimension } from "@rivtn/noseur";
import { ApiResponseData, BaseService, ConsusService, HostService, IHost, ITournamentEditionFaqs, IMember, IProfileWebsite, ITeam, ITournamentEdition, ITournamentEditionWebsite, IUploadedFile, MemberService, SanitizedResponse, TournamentService } from "../../../../../../../services";

export interface FaqsProps {
    profile: IMember;
    tournamentEdition: ITournamentEdition;
    tournamentEditionWebsite: ITournamentEditionWebsite;
}

function Faqs(props: FaqsProps) {

    const now = new Date().getTime();
    const { tournamentEdition } = props;
    const { width } = useScreenDimension();
    const tournamentService = TournamentService.getInstance();
    const [profile, _] = React.useState<IMember>(props.profile);
    const [faqs, setFaqs] = React.useState<ApiResponseData<ITournamentEditionFaqs>>({});
    const labels = i18nManager.Labels.host.manage_tournament.settings.about.faqs;
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const [tournamentEditionWebsite, setTournamentEditionWebsite] = React.useState<ITournamentEditionWebsite>(props.tournamentEditionWebsite);
    const maxSponsors = 5; // TODO FETCH FROM PLAN

    React.useEffect(fetchFaqs, []);

    return (<div className="profile-settings team-settings" style={{ padding: 0, boxShadow: "none" }}>
        <Checkbox scheme={Schemes.OMNEHOST} label={labels.enable_ad} defaultChecked={tournamentEditionWebsite.about_settings.enable_rivtn_ad_on_faqs} onChecked={(checked) => {
            tournamentService.updateTournamentEditionWebsite(tournamentEdition.external_id, { ...tournamentEditionWebsite, about_settings: { ...tournamentEditionWebsite.about_settings, enable_rivtn_ad_on_faqs: checked } }).then(({ }: SanitizedResponse<ITournamentEditionWebsite>) => {
                Siren.alert(labels.update_successful);
            }).catch(BaseService.reportError);
        }} />
        <hr style={{ marginBottom: 0 }} />
        <div className="pagination">
            {Elements.buildPaginator(faqs ?? {},
                ({ currentPage }, size) => Utils.updateSearchParamses({ "page": currentPage, size }, setSearchParams, fetchFaqs),
                parseInt(searchParams.get("page") ?? "1"), { margin: 15, marginBottom: 5 })}
            {(width > 800) && Elements.buildSorter(labels.sorts, (sort) => Utils.updateSearchParamses({ sort }, setSearchParams, fetchFaqs), labels.sorts[0].label)}
        </div>
        <div style={{ display: "flex", marginTop: 10, justifyContent: "space-between" }}>
            <Form scheme={Schemes.OMNEHOST} style={{ display: "flex" }}>
                <FormControl isFieldset label={labels.question} style={{ marginLeft: 15, width: 200 }}>
                    <TextInput defaultValue={searchParams.get("question") ?? ""} onInputComplete={(v) => {
                        Utils.updateSearchParams("question", v, setSearchParams, fetchFaqs);
                    }} />
                </FormControl>
                {(width > 800) && (<FormControl isFieldset label={labels.answer} style={{ marginLeft: 15, width: 200 }}>
                    <TextInput defaultValue={searchParams.get("answer") ?? ""} onInputComplete={(v) => {
                        Utils.updateSearchParams("answer", v, setSearchParams, fetchFaqs);
                    }} />
                </FormControl>)}
            </Form>
            <Button raised scheme={Schemes.OMNEHOST} text={labels.add_faq} style={{ marginTop: 10, padding: "10px 35px" }} onClick={() => addOrUpdateEvent()} />
        </div>
        <hr style={{ margin: "20px 0px" }} />
        {!faqs.content ? Elements.LOADING_STATE : (<div className="ho-events">
            {faqs.content?.map((faq) => {
                return (<div key={faq.external_id} className="ho-event" onClick={() => addOrUpdateEvent(faq)}>
                    <div className="details" style={{ margin: 0 }}>
                        <div className="r1 lined-limit">
                            <span className="t">{faq.question}</span>
                        </div>
                        <div className="dets lined-limit">{faq.answer}</div>
                    </div>
                </div>);
            })}
        </div>)}
        {faqs.content && !faqs.content.length && (Elements.buildEmptyListView("fa fa-circle", (<span>
            {labels.empty_label_parts.no_faq_found}{" "}
            <span className="link" onClick={() => addOrUpdateEvent()}>{labels.empty_label_parts.create}</span>{" "}
            {labels.empty_label_parts.an_faq}
        </span>)))}
    </div>);

    function fetchFaqs() {
        tournamentService.queryTournamentEditionFaqs(tournamentEdition.external_id, {
            answer: searchParams.get("answer"),
            question: searchParams.get("question"),
            sort: searchParams.get("sort") ?? "",
            page: searchParams.get("page") ?? "1",
            size: searchParams.get("size") ?? "100",
        }).then(({ apiResponse }: SanitizedResponse<ITournamentEditionFaqs>) => {
            setFaqs(apiResponse.data!);
        }).catch(BaseService.reportError);
    }

    function addOrUpdateEvent(faq: ITournamentEditionFaqs = {} as any) {
        const dialogLabels = labels.dialog.add_update;
        const dialogErrorRef = { current: null } as any;
        const mediaInputManageRef = { current: undefined as any };

        const dialog = ConfirmDialog({
            closable: true,
            style: { minWidth: 560, maxWidth: 560 },
            confirmLabel: (faq.external_id ? dialogLabels.update : dialogLabels.add),
            title: (faq.external_id ? dialogLabels.update_faq : dialogLabels.add_faq),
            footerElements: (faq.external_id
                ? (<Button raised scheme={Scheme.DANGER} text={dialogLabels.delete} style={{ padding: "10px 35px" }} onClick={() => {
                    dialog.hide(() => deleteEvent(faq));
                }} />)
                : undefined),
            content: (<div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", marginTop: 20 }}>
                <FormControl fill isFieldset label={dialogLabels.question} {...Elements.FCFL} style={{ flex: 1 }}>
                    <TextInput defaultValue={faq.question} onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        faq.question = v;
                    }} />
                </FormControl>
                <FormControl fill isFieldset label={dialogLabels.answer} {...Elements.FCFL} style={{ marginTop: 10 }}>
                    <TextAreaInput fill style={{ height: 180 }} defaultValue={faq.answer} onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        faq.answer = v;
                    }} />
                </FormControl>
                <div ref={dialogErrorRef} className="error" style={{ alignSelf: "flex-start" }}> </div>
            </div>),
            onConfirm: (button?: ButtonManageRef) => {
                dialogErrorRef.current!.innerText = " ";
                button?.setLoadingState(true);
                const method = (faq.external_id ? tournamentService.updateTournamentEditionFaq : tournamentService.createTournamentEditionFaq).bind(tournamentService);
                method(tournamentEdition.external_id, faq).then(() => {
                    Siren.alert(faq.external_id ? dialogLabels.update_successful : dialogLabels.add_successful);
                    dialog.hide();
                    fetchFaqs();
                }).catch((error: any) => {
                    dialogErrorRef.current!.innerText = error.errorMessage;
                    BaseService.reportError(error);
                }).finally(() => button?.setLoadingState(false));
                return false;
            }
        });
        dialog.show();
    }

    function deleteEvent(faq: ITournamentEditionFaqs) {
        const dialogLabels = labels.dialog.delete;
        const dialogErrorRef = { current: null } as any;

        const dialog = ConfirmDialog({
            closable: true,
            style: { maxWidth: 500 },
            title: dialogLabels.title,
            confirmScheme: Scheme.DANGER,
            desc: dialogLabels.desc.replace("${question}", faq.question),
            content: (<div ref={dialogErrorRef} className="error" style={{ alignSelf: "flex-start" }}> </div>),
            onConfirm: (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                dialogErrorRef.current!.innerText = " ";
                tournamentService.deleteTournamentEditionFaq(tournamentEdition.external_id, faq).then(() => {
                    Siren.alert(dialogLabels.successful);
                    dialog.hide();
                    fetchFaqs();
                }).catch((error: any) => {
                    dialogErrorRef.current!.innerText = error.errorMessage;
                    BaseService.reportError(error);
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

}

export default Faqs;
